import {
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useRegion } from '../auth/RegionContext';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { TransitionButton } from '../actions/TransitionButton';
import type { SubscriptionRow } from './subscriptionsModel';
import { PLUS_PERIOD_DAYS } from './subscriptionsModel';
import {
  STATUS_COLOR,
  STATUS_LABEL,
  formatAutoRenew,
  formatPeriodEnd,
} from './format';

/**
 * The per-band member list. Each row shows the member, their Plus state (grouped
 * and coloured for readability), auto-renew (the dispute-answering field, always
 * visible), the current period end and the processor-neutral billing reference.
 *
 * Two guarded, audited actions ride the EXISTING performStatusTransition +
 * withAudit chokepoint through TransitionButton (never a billing operation, just a
 * guarded field write on the users row), following the CashoutShow idiom:
 *  - Cancel renewal (active -> pending_cancel): editor capability. A member can
 *    do this themselves, so an admin doing it for them is low-stakes.
 *  - Comp Plus (confer a paid period at no wallet charge): super_admin capability.
 *    It confers real value for free, the same trust tier as cashout approve.
 */

const DAY = 86_400_000;

const StatusChip = ({ row }: { row: SubscriptionRow }) => (
  <Chip
    size="small"
    variant="outlined"
    color={STATUS_COLOR[row.plusStatus]}
    label={STATUS_LABEL[row.plusStatus]}
  />
);

export const SubscriptionsTable = ({
  members,
  now,
}: {
  members: SubscriptionRow[];
  now: number;
}) => {
  const { accessibleRegions } = useRegion();
  const { role } = useAccessContext();

  const canCancel = hasAccess(
    { role, accessibleRegions },
    { capability: 'subscriptions:cancel_renewal' },
  );
  const canComp = hasAccess(
    { role, accessibleRegions },
    { capability: 'subscriptions:comp' },
  );

  // A comped grant confers one fresh period ending PLUS_PERIOD_DAYS ahead of now.
  const compedPeriodEnd = new Date(now + PLUS_PERIOD_DAYS * DAY).toISOString();

  return (
    <Table size="small" aria-label="plus subscriptions">
      <TableHead>
        <TableRow>
          <TableCell>Member</TableCell>
          <TableCell>Status</TableCell>
          <TableCell>Auto-renew</TableCell>
          <TableCell>Period ends</TableCell>
          <TableCell>Reference</TableCell>
          <TableCell align="right">Actions</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {members.map((row) => {
          const isActive = row.plusStatus === 'active';
          const isLapsed =
            row.plusStatus === 'lapsed' ||
            row.plusStatus === 'lapsed_insufficient';
          return (
            <TableRow key={row.id}>
              <TableCell>
                <Typography variant="body2">{row.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.email}
                </Typography>
              </TableCell>
              <TableCell>
                <StatusChip row={row} />
              </TableCell>
              <TableCell>{formatAutoRenew(row.autoRenew)}</TableCell>
              <TableCell>{formatPeriodEnd(row.currentPeriodEnd)}</TableCell>
              <TableCell>
                <Typography variant="caption" color="text.secondary">
                  {row.billingReference ?? '—'}
                </Typography>
              </TableCell>
              <TableCell align="right">
                <Stack
                  direction="row"
                  spacing={1}
                  justifyContent="flex-end"
                  useFlexGap
                  flexWrap="wrap"
                >
                  {isActive && canCancel && (
                    <TransitionButton
                      resource="users"
                      capability="subscriptions:cancel_renewal"
                      id={row.id}
                      data={{ plusStatus: 'pending_cancel', autoRenew: false }}
                      previousData={{
                        id: row.id,
                        plusStatus: 'active',
                        autoRenew: true,
                      }}
                      label="Cancel renewal"
                      color="warning"
                      confirmTitle="Cancel renewal"
                      confirmContent="Turn auto-renew off for this member? Plus stays active until the current period ends, then lapses. The member could also do this themselves."
                      successMessage="Renewal cancelled"
                    />
                  )}
                  {isLapsed && canComp && (
                    <TransitionButton
                      resource="users"
                      capability="subscriptions:comp"
                      id={row.id}
                      data={{
                        plusStatus: 'active',
                        autoRenew: true,
                        currentPeriodEnd: compedPeriodEnd,
                      }}
                      previousData={{
                        id: row.id,
                        plusStatus: row.plusStatus,
                        autoRenew: row.autoRenew,
                        currentPeriodEnd: row.currentPeriodEnd,
                      }}
                      label="Comp Plus"
                      color="success"
                      confirmTitle="Comp a Plus period"
                      confirmContent="Grant this member a Plus period at no wallet charge? This confers paid value for free and is recorded against your account."
                      successMessage="Plus period comped"
                    />
                  )}
                </Stack>
              </TableCell>
            </TableRow>
          );
        })}
        {members.length === 0 && (
          <TableRow>
            <TableCell colSpan={6}>
              <Box sx={{ py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No Plus members in this market.
                </Typography>
              </Box>
            </TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  );
};
